'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Phone, Mail, MapPin } from 'lucide-react';
import { fetchSettings } from '@/lib/supabase/dinauto-service';

interface CompanyInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  org: string;
  hoursLines: { day: string; hours: string }[];
  discount7: string;
  discount14: string;
}

function parseHoursLines(raw: string): { day: string; hours: string }[] {
  if (!raw) return [
    { day: 'Monday – Friday', hours: '08:00 – 18:00' },
    { day: 'Saturday', hours: '09:00 – 15:00' },
    { day: 'Sunday', hours: 'Closed' },
  ];
  return raw.split('|').map((part) => {
    const [day, hours] = part.split(':').map((s) => s.trim());
    return { day: day || '', hours: hours || '' };
  });
}

export default function PublicFooter() {
  const [info, setInfo] = useState<CompanyInfo>({
    name: 'DIN AUTO AS',
    address: 'Munkebekken 400\n1061 Oslo, Norway',
    phone: '+47 4412 673 82',
    email: 'info@din-auto.no',
    org: '911696282',
    hoursLines: [
      { day: 'Monday – Friday', hours: '08:00 – 18:00' },
      { day: 'Saturday', hours: '09:00 – 15:00' },
      { day: 'Sunday', hours: 'Closed' },
    ],
    discount7: '10',
    discount14: '15',
  });

  useEffect(() => {
    fetchSettings().then((s) => {
      const rawAddr = s['company_address'] || '';
      const addrParts = rawAddr.split(',');
      const addressFormatted = addrParts.length >= 2
        ? `${addrParts[0].trim()}\n${addrParts.slice(1).join(',').trim()}`
        : rawAddr;

      setInfo({
        name: s['company_name'] || 'DIN AUTO AS',
        address: addressFormatted,
        phone: s['company_phone'] || '+47 4412 673 82',
        email: s['company_email'] || 'info@din-auto.no',
        org: s['company_org'] || '911696282',
        hoursLines: parseHoursLines(s['opening_hours'] || ''),
        discount7: s['discount_7_days_percent'] || '10',
        discount14: s['discount_14_days_percent'] || '15',
      });
    });
  }, []);

  const telHref = `tel:${info.phone.replace(/\s/g, '')}`;
  const mailHref = `mailto:${info.email}`;

  return (
    <footer className="bg-foreground text-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <AppLogo size={36} />
              <span className="font-black text-xl tracking-tight" style={{ fontWeight: 900 }}>DIN AUTO</span>
            </div>
            <p className="text-white/55 text-sm leading-relaxed mb-5">
              Quality car rental in Oslo since 2008. Transparent pricing, no hidden fees, free cancellation up to 24 hours before pickup.
            </p>
            <div className="flex gap-1">
              <span className="bg-primary text-foreground text-xs font-bold px-2 py-1">{info.discount7}% off {info.discount7 === '7' ? '7' : '7'}+ days</span>
              <span className="bg-white/10 text-white/70 text-xs font-bold px-2 py-1">{info.discount14}% off 14+ days</span>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-5">Navigation</h4>
            <ul className="space-y-3">
              {[
                { label: 'Home', href: '/' },
                { label: 'Our Cars', href: '/our-cars-gallery' },
                { label: 'Make a Reservation', href: '/reservation' },
                { label: 'Contact', href: '/contact' },
              ]?.map((l) => (
                <li key={`footer-link-${l?.href}`}>
                  <Link href={l?.href} className="text-white/60 hover:text-primary text-sm transition-colors">
                    {l?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-5">Contact</h4>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <MapPin size={16} className="text-primary shrink-0 mt-0.5" />
                <span className="text-white/60 text-sm whitespace-pre-line">{info.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone size={16} className="text-primary shrink-0 mt-0.5" />
                <a href={telHref} className="text-white/60 hover:text-primary text-sm transition-colors">{info.phone}</a>
              </li>
              <li className="flex gap-3">
                <Mail size={16} className="text-primary shrink-0 mt-0.5" />
                <a href={mailHref} className="text-white/60 hover:text-primary text-sm transition-colors">{info.email}</a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-white/40 mb-5">Opening Hours</h4>
            <ul className="space-y-2.5">
              {info.hoursLines.map((h) => (
                <li key={`hours-${h.day}`} className="flex justify-between text-sm">
                  <span className="text-white/50">{h.day}</span>
                  <span className={h.hours === 'Closed' ? 'text-white/30' : 'text-white/80 font-600'}>{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-white/8">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/35 text-xs">
            © 2026 {info.name} — Org.nr. {info.org} — {info.address.replace('\n', ', ')}
          </p>
          <div className="flex items-center gap-4">
            <Link href="/privacy-policy" className="text-white/40 hover:text-white/70 text-xs transition-colors">
              Privacy Policy
            </Link>
            <span className="text-white/20 text-xs">·</span>
            <Link href="/terms" className="text-white/40 hover:text-white/70 text-xs transition-colors">
              Terms &amp; Conditions
            </Link>
          </div>
          <p className="text-white/25 text-xs">Free cancellation · No hidden fees · Transparent NOK pricing</p>
        </div>
      </div>
    </footer>
  );
}